// Auth-related API calls: login, register, profile update.
// Wired up to the real Today Talks backend - all three are POST, token-based
// (the token comes back from login/register and is sent as a Bearer token
// on update-profile).
const BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://todaytalks.mpdatahub.com/api";

/**
 * Pulls a readable message out of a Laravel-style error response, which is
 * either `{ message }` or `{ message, errors: { field: [msg, ...] } }` from
 * the validator.
 */
function extractErrorMessage(body, fallbackMessage) {
  if (!body) return fallbackMessage;
  if (body.errors && typeof body.errors === "object") {
    const firstField = Object.keys(body.errors)[0];
    const firstMessage = firstField && Array.isArray(body.errors[firstField]) && body.errors[firstField][0];
    if (firstMessage) return firstMessage;
  }
  return body.message || fallbackMessage;
}

/**
 * Shared request helper for auth calls.
 * - body: plain object (sent as JSON) or a FormData instance (sent as-is,
 *   required whenever a file field like profile_image/profile_document is
 *   involved).
 * - token: when set, sent as `Authorization: Bearer <token>`.
 */
async function request(endpoint, { body, token } = {}) {
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const headers = {};
  if (!isFormData) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method: "POST",
    headers,
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });

  let json = null;
  try {
    json = await res.json();
  } catch (err) {
    // no JSON body - fine, handled below
  }

  if (!res.ok) {
    throw new Error(extractErrorMessage(json, `Request failed (${res.status})`));
  }

  return json || {};
}

/**
 * The backend's success-response shape for auth endpoints isn't fully
 * documented, so this accepts the common variants (token at the top level
 * or under `data`, user under `user` or `data`) and maps them to one shape
 * the rest of the app relies on: { token, user }.
 */
function normalizeAuthPayload(json) {
  const token = json.token || json.access_token || json?.data?.token || json?.data?.access_token || null;
  const rawUser = json.user || json?.data?.user || (json.data && json.data.id ? json.data : null) || null;

  const user = rawUser
    ? {
      id: rawUser.id ?? rawUser._id ?? null,
      name: rawUser.name || "",
      email: rawUser.email || "",
      mobile: rawUser.mobile || "",
      dob: rawUser.dob || "",
      location: rawUser.location || "",
      role: rawUser.role || "",
      profileImage: rawUser.profile_image || rawUser.profile_image_url || rawUser.profileImage || "",
    }
    : null;

  return { token, user };
}

/**
 * POST /login  { email, password }
 */
export async function loginUser({ email, password }) {
  const json = await request("/login", { body: { email, password } });
  const { token, user } = normalizeAuthPayload(json);
  if (!token || !user) throw new Error("Unexpected response from server. Please try again.");
  return { token, user };
}

/**
 * POST /register  { name, email, mobile, password, dob, location, role?, profile_image? }
 * Sent as multipart/form-data since profile_image is an optional file upload.
 */
export async function registerUser({ name, email, mobile, password, dob, location, role, profileImage }) {
  const form = new FormData();
  form.append("name", name);
  form.append("email", email);
  form.append("mobile", mobile);
  form.append("password", password);
  form.append("dob", dob);
  if (location) form.append("location", location);
  if (role) form.append("role", role);
  if (profileImage) form.append("profile_image", profileImage);

  const json = await request("/register", { body: form });
  const { token, user } = normalizeAuthPayload(json);
  if (!token || !user) throw new Error("Unexpected response from server. Please try again.");
  return { token, user };
}

/**
 * POST /update-profile  { name?, email?, mobile?, dob?, location?, profile_document? }
 * Requires the Bearer token from login/register. Sent as multipart/form-data
 * since profile_document is an optional PDF upload.
 */
export async function updateProfile({ name, email, mobile, dob, location, profileDocument, profileImage }, token) {
  const form = new FormData();
  if (name) form.append("name", name);
  if (email) form.append("email", email);
  if (mobile) form.append("mobile", mobile);
  if (dob) form.append("dob", dob);
  if (location) form.append("location", location);
  if (profileDocument) form.append("profile_document", profileDocument);
  if (profileImage) form.append("profile_image", profileImage);

  const json = await request("/update-profile", { body: form, token });
  const { user } = normalizeAuthPayload(json);
  // Some backends only return a confirmation message on update, not the
  // full user record - fall back to merging in what we just submitted.
  return user || { name, email, mobile, dob, location };
}
